import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from 'react-router-dom';

import { Milestone } from 'lucide-react';
import styles from "./StartIndependantGame.module.scss";
import { DEBUG } from '../../utils/constants';

// import useSafeAsyncEffect from '../../utils/hooks/useSafeAsyncEffect';
import useQuestionsManager from '../../utils/hooks/useQuestionsManager';
import LoadingIndicator from '../../utils/LoadingIndicator/LoadingIndicator';
import SmartRenderer from '../../utils/SmartRenderer/SmartRenderer';

import QuestionsInde from "./QuestionsInde/QuestionsInde";
import QuestionFooter from "../QuestionFooter/QuestionFooter";

// APIS 
import { getGame } from '../../utils/api/gamesAPI';
import { getGameInfos } from '../../utils/api/gameInfosAPI';
import { getAllQuestionsByGame, getAbortedQuestions } from '../../utils/api/questionsIndeAPI';
import { getAllRepLibresByQuestion } from '../../utils/api/reponsesLibresIndeAPI';
import { getAllRepQcmByQuestion } from '../../utils/api/reponsesQcmIndeAPI';
import { createProgressInde, achieveProgression } from '../../utils/api/progressionIndeAPI';

import { useGame } from "../../context/GameContext";
import { useAuth } from '../../context/AuthContext';
import { LanguageContext } from '../../context/LanguageContext';
import translations from '../../utils/translations/StartIndependantGameTranslations';


const debug = DEBUG;

const StartIndependantGame = ({ gameId }) => {

    const { user } = useAuth();

    // Utilisation du contexte de langue global et accès simplifié aux traductions
    const { language, changeLanguage } = useContext(LanguageContext);
    const t = translations[language] || translations.fr;

    const navigate = useNavigate();
    const { startNewGame, clearGameData } = useGame(); // infosData[0].id (équivalent de parcoursId et gameId pour identifier le jeu)

// --------------------- STATES --------------------------

    const [game, setGame] = useState(null); // Jeu sélectionné
    const [gameInfos, setGameInfos] = useState(null); // Toutes les langues disponibles pour le jeu sélectionné
    const [infosData, setInfosData] = useState(null); // La langue sélectionnée
    const [repLibres, setRepLibres] = useState([]); // L'ensemble des réponses type libres des questions du jeu sélectionné
    const [repQcm, setRepQcm] = useState([]); // L'ensemble des réponses type qcm des questions du jeu sélectionné

    const [loading, setLoading] = useState(true); // L'état de chargement ou non

    const [showCodeModal, setShowCodeModal] = useState(true); // L'affichage de la modale de démarrage
    const [showIntro, setShowIntro] = useState(true); // L'affichage de l'intro (début par défaut)
    const [isReplay, setIsReplay] = useState(false);
    const [showOutro, setShowOutro] = useState(false); // L'affichage de l'outro en fonction de l'état du jeu
    const [showEndConfirmation, setShowEndConfirmation] = useState(false); // Modale de confirmation de fin du jeu

    const [inputCode, setInputCode] = useState(""); // Le code entré par l'utilisateur
    const [error, setError] = useState(""); // Un message d'erreur le cas échéant
    const [nbPlayers, setNbPlayers] = useState(1); // Nombre de joueurs (1 par défaut)

    // -- reprise/abandon (mêmes principes que VillageChoice)
    const [showResumePrompt, setShowResumePrompt] = useState(false);
    const [showQuitModal, setShowQuitModal] = useState(false);
    const [resumeError, setResumeError] = useState(""); // si jamais on ne peut pas recharger proprement


    const [langNotAvailable, setLangNotAvailable] = useState(false); // Le fait qu'il n'existe pas de langue pour un jeu sélectionné ??

    const { questions, initializeQuestions, setQuestionAchieved, startQuestionTimer, updateQuestionField, saveQuestionsToStorage, clearQuestions } = useQuestionsManager("inde", infosData?.[0]?.id);

// --------------------- EFFECTS/RAPPELS --------------------------
    
    // useEffect(() => {
        
    //     const fetchInitialData = async () => {
    //         try {
    //             setLoading(true);
                
    //             // Récupérer simultanément le jeu et les infos du jeu
    //             const [gameData, gameInfosData] = await Promise.all([
    //                 getGame(gameId),
    //                 getGameInfos(gameId)
    //             ]);
                
    //             // Mettre à jour l'état avec les données récupérées
    //             setGame(gameData); // Données du jeu (nom, code...)
    //             if (debug) console.log("Jeu sélectionné : ", gameData);

    //             if (!Array.isArray(gameInfosData)) {
    //                 if (debug) console.warn("Réponse inattendue (pas un tableau) :", gameInfosData);
    //                 setGameInfos([]);
    //                 setLangNotAvailable(true);
    //                 return;
    //             }
    //             const activeGameInfos = gameInfosData.filter(info => info.active === 1 || info.active === true);
                
    //             setGameInfos(activeGameInfos);
    //             if (debug) console.log("Toutes les langues du jeu sélectionné : ", gameInfosData);
                
    //             // Sélectionner les infos dans la langue actuelle
    //             if (gameInfosData && gameInfosData.length > 0) {
    //                 const initialInfos = gameInfosData.find(info => info.lang === language) || gameInfosData[0]; // On cherche dans la langue sélectionnée ou à défaut la première langue dispo...
    //                 if (initialInfos) {
    //                     setInfosData([initialInfos]);
    //                     if (debug) console.log("La langue sélectionnée : ", initialInfos);
    //                     await loadQuestionsAndAnswers(initialInfos.id);
    //                 } else {
    //                     setLangNotAvailable(true); // Déclenche le choix de langue
    //                 }
    //             }
    //         } catch (error) {
    //             if (debug) console.error("Erreur lors de la récupération des données initiales", error);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchInitialData();
    // }, [gameId, language]); // Ajouter language comme dépendance
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
            setLoading(true);

            // Jeu + infos
            const [gameData, gameInfosData] = await Promise.all([
                getGame(gameId),
                getGameInfos(gameId)
            ]);

            setGame(gameData);
            if (debug) console.log("Jeu sélectionné : ", gameData);

            if (!Array.isArray(gameInfosData)) {
                if (debug) console.warn("Réponse inattendue (pas un tableau) :", gameInfosData);
                setGameInfos([]);
                setLangNotAvailable(true);
                return;
            }
            const activeGameInfos = gameInfosData.filter(info => info.active === 1 || info.active === true);
            setGameInfos(activeGameInfos);
            if (debug) console.log("Toutes les langues du jeu sélectionné : ", gameInfosData);

            if (gameInfosData && gameInfosData.length > 0) {
                const initialInfos =
                gameInfosData.find(info => info.lang === language) || gameInfosData[0];
                if (initialInfos) {
                setInfosData([initialInfos]);
                if (debug) console.log("La langue sélectionnée : ", initialInfos);
                // ⚠️ NE CHARGE PAS encore les questions ici
                } else {
                setLangNotAvailable(true);
                }
            }

            // ----- Vérifie si un jeu est en cours (même modèle que VillageChoice)
            const savedGameId = localStorage.getItem("currentGameId");
            const savedInfoId = localStorage.getItem("currentGameInfoId"); // équiv. currentVersionId dans ton autre flux
            if(debug) console.log(savedGameId);
            if(debug) console.log(savedInfoId);

            if (savedGameId && savedInfoId) {
                if(debug) console.log("Il y a une partie en cours");
                // Proposer de reprendre
                setShowResumePrompt(true);
                setShowCodeModal(false);
                setShowIntro(false);
            } else {
                if(debug) console.log("Il n'y a pas de partie en cours");
                // Pas de reprise → on laisse le flux normal (modale code)
                setShowResumePrompt(false);
                setShowCodeModal(true);
                setShowIntro(false); // l’intro ne s’affiche qu’après validation du code
            }
            } catch (error) {
            if (debug) console.error("Erreur lors de la récupération des données initiales", error);
            } finally {
            setLoading(false);
            }
        };

        fetchInitialData();
    }, [gameId, language]);



    // Pour afficher ou non la modale de démarrage et l'intro, si un parcours est en cours..
    useEffect(() => { 
        if (questions && questions.length > 0) {
            
            const hasStarted = questions.some(q => q.startTime !== null); // Si au moins une question a un startTime défini → le joueur a commencé
            if (hasStarted) {
                setShowCodeModal(false);
                setShowIntro(false); 
            }
            
          }
    }, [questions]);


    // Fonction utilitaire pour charger les questions et réponses associées
    const loadQuestionsAndAnswers = async (gameInfoId) => {
        try {
            const localQuestions = localStorage.getItem(`questions_inde_${gameInfoId}`);
            const localRepLibres = localStorage.getItem(`repLibres_inde_${gameInfoId}`);
            const localRepQcm = localStorage.getItem(`repQcm_inde_${gameInfoId}`);

            if (localQuestions && localRepLibres && localRepQcm) {
                if (debug) console.log("✅ Chargement des données trouvées dans localStorage");

                // Charger directement depuis le local
                const questionsData = JSON.parse(localQuestions);
                initializeQuestions(questionsData); // Stocke et enrichit via le hook

                setRepLibres(JSON.parse(localRepLibres));
                setRepQcm(JSON.parse(localRepQcm));
            } else {
                if (debug) console.log("📡 Pas de données locales, récupération depuis API...");
                const questionsData = await getAllQuestionsByGame(gameInfoId);
                
                // Charger les réponses libres et QCM en parallèle pour optimiser
                const [repLibresData, repQcmData] = await Promise.all([
                    // Réponses pour les questions libres
                    Promise.all(
                        questionsData
                            .filter(question => question.type === "libre")
                            .map(async question => {
                                const reponse = await getAllRepLibresByQuestion(question.id);
                                return {
                                    id: reponse.id,
                                    possibilities: reponse.possibilities,
                                    idQuestion: question.id
                                };
                            })
                    ),
                    // Réponses pour les questions QCM
                    Promise.all(
                        questionsData
                            .filter(question => question.type === "qcm")
                            .map(async question => {
                                const reponses = await getAllRepQcmByQuestion(question.id);
                                const arrayReponses = Array.isArray(reponses) ? reponses : [];

                                return arrayReponses.map(reponse => ({
                                id: reponse.id,
                                idQuestion: question.id,
                                txt: reponse.txt,
                                img: reponse.img,
                                isTrue: reponse.is_true
                                }));
                            })
                    ).then(arrays => arrays.flat())
                ]);

                const validQuestions = questionsData.filter(q => {
                    if (q.type === "libre") {
                        return repLibresData.some(rep => rep.idQuestion === q.id && rep.possibilities.length > 0);
                    } else if (q.type === "qcm") {
                        return repQcmData.some(rep => rep.idQuestion === q.id);
                    }
                    return true;
                });

                initializeQuestions(validQuestions);
            
                setRepLibres(repLibresData);
                setRepQcm(repQcmData);

                localStorage.setItem(`repLibres_inde_${gameInfoId}`, JSON.stringify(repLibresData));
                localStorage.setItem(`repQcm_inde_${gameInfoId}`, JSON.stringify(repQcmData));
            }
        } catch (error) {
            if (debug) console.error("Erreur lors du chargement des questions et réponses", error);
        }
    };

    // Effet pour gérer le changement de langue global
    // useEffect(() => {
    //     // Ne pas exécuter si les infos du jeu ne sont pas encore chargées
    //     if (!gameInfos) return;
    //     if(debug) console.log("Changement de langue global:", language);

    //     // Message (bloquant) puis redirection
    //     window.alert(t.langChangeRedirect || "La langue a été changée. Vous allez être redirigé vers l’accueil.");
    //     navigate("/");
    // }, [language, navigate]); // Réagir au changement de langue global

    const purgeIndeCache = (gameInfoId, gameId) => {
      try {
        // Progression liée au jeu "indé"
        localStorage.removeItem(`progression_inde_${gameInfoId}`); // Vérifier qu'elle est bien créée avec gameInfoId !!
        // Sélection courante
        localStorage.removeItem("currentGameId");
        localStorage.removeItem("currentGameInfoId");

        localStorage.removeItem(`questions_inde_${gameInfoId}`);
        localStorage.removeItem(`repLibres_inde_${gameInfoId}`);
        localStorage.removeItem(`repQcm_inde_${gameInfoId}`);

        localStorage.removeItem(`questions_inde_undefined`);

        // Langue mémorisée pour ce jeu (si utilisée auparavant)
        localStorage.removeItem(`gameLang-${gameInfoId}`);
      } catch (e) {
        if (debug) console.warn("Purge inde: erreur non bloquante", e);
      }
    };


// --------------------- HANDLERS --------------------------

    // const handleCodeSubmit = () => {
    //     if (inputCode.trim() === game.code) {
    //         setShowCodeModal(false);
    //         setShowIntro(true);
    //     } else {
    //         setError(t.codeError);
    //     }
    // };
    const handleCodeSubmit = async () => {
        if (inputCode.trim() === game.code) {
            setError("");
            try {
            // maintenant on charge
            await loadQuestionsAndAnswers(infosData[0].id);
            setShowCodeModal(false);
            setShowIntro(true);
            } catch (e) {
            if (debug) console.error("Erreur chargement Q/R après code :", e);
            setError(t.errorLoadingGame || "Erreur de chargement des données du jeu.");
            }
        } else {
            setError(t.codeError);
        }
    };

    const handleResume = async () => {
        try {
            setResumeError("");
            const savedInfoId = localStorage.getItem("currentGameInfoId");
            if (!savedInfoId) {
            setResumeError(t.resumeLoadError || "Impossible de reprendre cette partie.");
            return;
            }
            // charge en mémoire depuis cache/API comme d’hab
            await loadQuestionsAndAnswers(savedInfoId);

            // on repart directement en jeu (pas d’intro)
            setShowResumePrompt(false);
            setShowCodeModal(false);
            setShowIntro(false);
        } catch (e) {
            if (debug) console.error("Reprise: erreur chargement Q/R", e);
            setResumeError(t.resumeLoadError || "Impossible de reprendre cette partie.");
        }
    };

    const handleQuit = () => {
        setShowQuitModal(true);
    };

    const confirmQuitResume = () => {
        const savedInfoId = localStorage.getItem("currentGameInfoId");
        purgeIndeCache(savedInfoId, gameId);
        clearQuestions();
        // On revient au flux normal (saisie code)
        setShowQuitModal(false);
        setShowResumePrompt(false);
        setShowCodeModal(true);
        setShowIntro(false);
    };


    const handleLanguageSelect = (selectedLang) => {
        changeLanguage(selectedLang); // Met à jour la langue globale via le contexte
        setLangNotAvailable(false); // Cache la modale
    };

    // Déclenché au début du jeu, après avoir validé le code et cliqué sur démarrer
    const handleStartGame = async () => {
        if (debug) console.log("Démarrage d'un nouveau jeu, infosDataId : ", infosData[0].id)

        try {
            const userId = user?.id;
            if (!userId) throw new Error("Utilisateur non connecté");

            const existingProgressionId = localStorage.getItem(`progression_inde_${infosData[0].id}`);
            if (debug) console.log("Progression enregistrée en localStorage : ", existingProgressionId);

            if (!existingProgressionId) {
                
                const gameInfosId = infosData[0].id;

                const res = await createProgressInde(userId, gameInfosId, nbPlayers);

                if (res?.success && res.id) {
                    localStorage.setItem(`progression_inde_${infosData[0].id}`, res.id);
                    if (debug) console.log("Progression Inde : clé créée en localStorage :", res.id);
                } else {
                    if (debug) console.error("Erreur lors de la création de la progression Inde :", res);
                }
            } else {
                if (debug) console.log("Progression : clé déjà existante :", existingProgressionId);
            }

            startNewGame(infosData[0].id, gameId, "inde"); // conserve le GameContext
            setShowIntro(false);
        } catch (error) {
            if (debug) console.error("Erreur dans handleStartGame :", error);
        }
    };

    const handleFinishGame = () => { // Active l'écran de fin
        setShowOutro(true);  
    };

    const handleEndGame = async () => { // Lance la fin effective du jeu en ouvrant la fenêtre de confirmation
        const progressionId = localStorage.getItem(`progression_inde_${infosData[0].id}`);
        const userId = user?.id;
        let isClosed = false;
        if (progressionId) {
            const abortedQuestions = false;
            if (!abortedQuestions || abortedQuestions.length === 0) {
                const res = await achieveProgression(progressionId, infosData[0].id, userId);
                if (debug) console.log("Réponse serveur :", res);

                if (res?.success) {
                    if (debug) console.log("Partie clôturée côté serveur");
                    isClosed = true;
                } else {
                    if (debug) console.log("Partie non clôturée : échec côté serveur");
                }
            } else {
                if (debug) console.log("Partie incomplète, non clôturée");
            }
        }

        if (isClosed) {
            setShowEndConfirmation(true);
            localStorage.removeItem(`progression_inde_${infosData[0].id}`);
        } else {
            // Placeholder, à implémenter selon retour du serveur...
            setShowEndConfirmation(true);
            localStorage.removeItem(`progression_inde_${infosData[0].id}`);
        }
    };

    const confirmEndGame = () => { // Opérations de fin du jeu avant redirection

        localStorage.removeItem(`questions_inde_${infosData?.[0]?.id}`);
        localStorage.removeItem(`repLibres_inde_${infosData?.[0]?.id}`);
        localStorage.removeItem(`repQcm_inde_${infosData?.[0]?.id}`);

        localStorage.removeItem(`questions_inde_undefined`);

        clearQuestions();
        clearGameData("inde"); // Supprime uniquement les données du jeu sans toucher aux tokens d'authentification
        navigate("/");
    };
      

// --------------------- RENDERINGS --------------------------

    // Affichage du chargement
    if (loading && !game) {
        return <LoadingIndicator />;
    }

    // Vérification des erreurs
    if (!game && !loading) {
        return <p>{t.errorLoadingGame}</p>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.questionHeader}>
                <div className={styles.iconContainer}><span className={styles.icon}><Milestone /></span></div>
                <p><strong>{game ? game.nom : t.loading}</strong></p>
            </div>

            {showResumePrompt && (
                <div className={styles.enCoursContainer}>
                    <div className={styles.enCours}>
                    <p>{t.resumeMessage || "Une partie est en cours. Voulez-vous la reprendre ?"}</p>
                    {resumeError && <p className={styles.errorText}>{resumeError}</p>}
                    <div className={styles.buttonGroup}>
                        <button className={styles.resumeButton} onClick={handleResume}>
                        {t.resumeButton || "Reprendre"}
                        </button>
                        <button className={styles.quitButton} onClick={handleQuit}>
                        {t.quitButton || "Abandonner"}
                        </button>
                    </div>
                    </div>
                </div>
            )}


            {showCodeModal && game && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <p className={styles.introText}>
                            {t.message}
                        </p>
                        <input
                            type="text"
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value)}
                            className={styles.codeInput}
                            placeholder={t.accessCode}
                        />
                        {error && <p className={styles.errorText}>{error}</p>}

                        {langNotAvailable && (
                            <div className={styles.modalOverlay}>
                                <div className={styles.modal}>
                                    {gameInfos.length > 0 ? (
                                        <>
                                            <p>{t.langNotAvailable}</p>
                                            <p>{t.changeLangInstruction}</p>
                                            <div className={styles.languageOptions}>
                                                {gameInfos.map((info) => (
                                                    <button 
                                                        key={info.lang} 
                                                        className={styles.languageButton} 
                                                        onClick={() => handleLanguageSelect(info.lang)}
                                                    >
                                                        {info.lang}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <p>{t.noLangAvailable}</p>
                                    )}
                                    <button className={styles.returnButton} onClick={() => navigate("/")} >
                                        {t.backToHome}
                                    </button>
                                </div>
                            </div>
                        )}

                        <p className={styles.introText}>
                            {t.howManyPlayers}
                        </p>

                        <input 
                            type="number"
                            className={styles.nbPlayersInput}
                            value={nbPlayers}
                            onChange={e => setNbPlayers(e.target.value)}
                            placeholder={t.nbPlayersPlaceholder || "Nombre de joueurs"}
                        />
                        
                        <button 
                            className={styles.startButton}
                            onClick={handleCodeSubmit}
                        >
                            {t.validate}
                        </button>
                        <button 
                            className={styles.returnButton}
                            onClick={() => navigate("/")}
                        >
                            {t.backToHome}
                        </button>
                    </div>
                </div>
            )}
            
            {!showCodeModal && !showResumePrompt && (
                showIntro ? (
                    <div className={styles.modalOverlay}>
                        <div className={styles.modal}>
                            {infosData && infosData.length > 0 ? (
                                <>
                                    {/* <MarkdownRenderer content={infosData[0].intro} /> */}
                                    <div className="rt-content">
                                        <SmartRenderer content={infosData[0].intro} />
                                    </div>

                                    {!isReplay ? (
                                        <button className={styles.startButton} onClick={handleStartGame}>
                                            {t.startButton}
                                        </button>
                                    ) : (
                                        <button 
                                            className={styles.startButton}
                                            onClick={() => {
                                                setShowIntro(false);
                                                setIsReplay(false);
                                            }}
                                        >
                                            Retour au jeu
                                        </button>
                                    )}
                                    
                                </>
                            ) : (
                                <p>{t.loadingInfo}</p>
                            )}
                        </div>
                    </div>
                ) : showOutro ? (  
                    <>
                    <div className={styles.modalOverlay}>
                      <div className={styles.modal}>
                        {/* <MarkdownRenderer content={infosData[0].outro} /> */}
                        <div className="rt-content">
                            <SmartRenderer content={infosData[0].outro} />
                        </div>

                        {/* Affichage des indices trouvés */}
                        {/* {questions.length > 0 && (
                            <div className={styles.clueContainer}>
                                <ul className={styles.clueList}>
                                    {questions.map((question, index) => (
                                        (question.cle_indice || question.img_indice) && (
                                            <li key={index} className={styles.clueItem}>
                                                {question.cle_indice && <p>{question.cle_indice}</p>}
                                                {question.img_indice && <img src={`https://hatannapi.scriptales.com/${question.img_indice}`} alt={`Indice ${index + 1}`} className={styles.clueImage} />}
                                            </li>
                                        )
                                    ))}
                                </ul>
                            </div>
                        )} */}
                        <button 
                          className={styles.endButton}
                          onClick={handleEndGame}  // Une fonction pour réinitialiser et rediriger
                        >
                          {t.backToHome}
                        </button>
                      </div>
                    </div> 

                    {showEndConfirmation && (
                        <div className={styles.modalOverlay}>
                            <div className={styles.modal}>
                                <p className={styles.modalText}>{t.confirmEndGameMessage}</p>
                                <div className={styles.buttonGroup}>
                                <button className={styles.cancelButton} onClick={() => setShowEndConfirmation(false)}>
                                    {t.cancel}
                                </button>
                                <button className={styles.confirmButton} onClick={confirmEndGame}>
                                    {t.confirm}
                                </button>
                                </div>
                            </div>
                        </div>
                    )}
                    </>
                ) : (
                    <QuestionsInde 
                        game={game}
                        questions={questions}
                        repLibres={repLibres}
                        repQcm={repQcm}
                        onFinishGame={handleFinishGame}
                        setQuestionAchieved={setQuestionAchieved}
                        startQuestionTimer={startQuestionTimer}
                        saveQuestionsToStorage={saveQuestionsToStorage}
                        updateQuestionField={updateQuestionField}
                    />
                )
            )}

            {showQuitModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                    <p className={styles.modalText}>
                        {t.quitConfirmationMessage || "Voulez-vous vraiment abandonner cette partie ?"}
                    </p>
                    <div className={styles.buttonGroup}>
                        <button className={styles.cancelButton} onClick={() => setShowQuitModal(false)}>
                        {t.cancelQuitButton || "Annuler"}
                        </button>
                        <button className={styles.confirmButton} onClick={confirmQuitResume}>
                        {t.confirmQuitButton || "Confirmer"}
                        </button>
                    </div>
                    </div>
                </div>
            )}

            
            <QuestionFooter type="inde" gId={gameId} setShowIntro={setShowIntro} setIsReplay={setIsReplay} gIdInfos={infosData?.[0]?.id} />
        </div>
    );
};

export default StartIndependantGame;